import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeft, History as HistoryIcon, RotateCcw, Trash2 } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  evaluateExpression,
  type AngleMode,
  type DisplayMode,
} from "@/lib/calculators/scientific";

/* ------------ Strings ------------ */

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Scientific Calculator", bn: "সাইন্টিফিক ক্যালকুলেটর" },
  desc: {
    en: "Free Casio fx-991 style scientific calculator with full trigonometry, logarithm, exponents, factorials and history. Built for SSC, HSC and university students in Bangladesh.",
    bn: "Casio fx-991 ধাঁচের পূর্ণাঙ্গ সাইন্টিফিক ক্যালকুলেটর — ত্রিকোণমিতি, লগারিদম, ঘাত, ফ্যাক্টোরিয়াল ও হিস্ট্রি সহ। SSC, HSC ও বিশ্ববিদ্যালয়ের শিক্ষার্থীদের জন্য।",
  },
  angle: { en: "Angle", bn: "কোণ" },
  display: { en: "Display", bn: "ডিসপ্লে" },
  fix: { en: "FIX digits", bn: "FIX দশমিক" },
  history: { en: "History", bn: "হিস্ট্রি" },
  clearHistory: { en: "Clear history", bn: "হিস্ট্রি মুছুন" },
  noHistory: { en: "No history yet — your calculations will appear here.", bn: "কোনো হিস্ট্রি নেই — আপনার গণনা এখানে দেখাবে।" },
  bnNumerals: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  shiftActive: { en: "SHIFT", bn: "শিফট" },
  expressionPh: { en: "Type or tap to start…", bn: "টাইপ করুন বা বাটনে চাপ দিন…" },

  howTitle: { en: "How to use the scientific calculator", bn: "সাইন্টিফিক ক্যালকুলেটর কীভাবে ব্যবহার করবেন" },
  howBody: {
    en: "Type your expression directly using the keyboard, or tap the on-screen buttons. Use the SHIFT key (top-left) to access the secondary functions printed above each button — for example SHIFT then sin gives you sin⁻¹ (inverse sine). Press = or Enter to evaluate. AC clears everything; DEL deletes the last character. Your last answer is available as Ans.",
    bn: "কীবোর্ডে সরাসরি এক্সপ্রেশন লিখুন, অথবা স্ক্রিনের বাটনে চাপ দিন। প্রতিটি বাটনের উপরের সেকেন্ডারি ফাংশন পেতে SHIFT (উপরে বামে) ব্যবহার করুন — যেমন SHIFT তারপর sin চাপলে sin⁻¹ (ইনভার্স সাইন) আসে। ফলাফল পেতে = বা Enter চাপুন। AC সব পরিষ্কার করে, DEL শেষ অক্ষর মুছে দেয়। শেষ ফলাফল Ans হিসেবে পাওয়া যায়।",
  },
  refTitle: { en: "Function reference", bn: "ফাংশন তালিকা" },
  modeTitle: { en: "DEG vs RAD — when to use each", bn: "DEG বনাম RAD — কখন কোনটি" },
  modeBody: {
    en: "Use DEG (degrees) for SSC and most HSC trigonometry questions where angles are written as 30°, 60°, 90°. Use RAD (radians) for HSC calculus and university physics where angles are expressed in terms of π — for example sin(π/6). Switching modes mid-problem changes the result, so confirm the indicator on the display before evaluating.",
    bn: "SSC ও বেশিরভাগ HSC ত্রিকোণমিতিতে কোণ থাকে ৩০°, ৬০°, ৯০° — তখন DEG ব্যবহার করুন। HSC ক্যালকুলাস ও বিশ্ববিদ্যালয়ের পদার্থবিজ্ঞানে কোণ π এর মাধ্যমে লেখা হয় (যেমন sin(π/6)) — তখন RAD ব্যবহার করুন। মাঝপথে মোড পাল্টালে ফলাফল ভিন্ন হবে, তাই হিসাবের আগে ডিসপ্লেতে মোড দেখে নিন।",
  },
  shortcutsTitle: { en: "Keyboard shortcuts", bn: "কীবোর্ড শর্টকাট" },
  sscTitle: { en: "For SSC students", bn: "SSC শিক্ষার্থীদের জন্য" },
  sscBody: {
    en: "Common board-exam tools: square root √ for Pythagoras, sin/cos/tan in DEG mode for trigonometry chapters, log for logarithm questions, π for area and circumference, n! for permutations and combinations, and parentheses for BODMAS expressions.",
    bn: "বোর্ড পরীক্ষার সাধারণ ব্যবহার: পিথাগোরাসের জন্য √, ত্রিকোণমিতির অধ্যায়ে DEG মোডে sin/cos/tan, লগারিদম প্রশ্নে log, ক্ষেত্রফল ও পরিধির জন্য π, বিন্যাস ও সমাবেশে n!, এবং BODMAS এক্সপ্রেশনের জন্য বন্ধনী।",
  },
  hscTitle: { en: "For HSC students", bn: "HSC শিক্ষার্থীদের জন্য" },
  hscBody: {
    en: "Physics: vector components with sin/cos in DEG, exponents with 10^x for orders of magnitude, ln and e^x for radioactive decay and capacitor charging. Chemistry: log for pH, 10^x for hydrogen-ion concentration. Math: RAD mode for derivatives and integrals involving trigonometry.",
    bn: "পদার্থবিজ্ঞান: ভেক্টরের উপাংশে DEG মোডে sin/cos, ক্রমের জন্য 10^x, তেজস্ক্রিয় ক্ষয় ও ক্যাপাসিটর চার্জিংয়ে ln ও e^x। রসায়ন: pH নির্ণয়ে log, হাইড্রোজেন আয়নের ঘনত্বে 10^x। গণিত: ত্রিকোণমিতিক অন্তরীকরণ ও সমাকলনের জন্য RAD মোড।",
  },
};

const FUNC_REF: { fn: string; meaning: Bilingual; example: string }[] = [
  { fn: "sin, cos, tan", meaning: { en: "Trigonometric functions", bn: "ত্রিকোণমিতিক ফাংশন" }, example: "sin(30) = 0.5 (DEG)" },
  { fn: "asin, acos, atan", meaning: { en: "Inverse trigonometry (SHIFT)", bn: "ইনভার্স ত্রিকোণমিতি (SHIFT)" }, example: "asin(0.5) = 30 (DEG)" },
  { fn: "log", meaning: { en: "Base-10 logarithm", bn: "১০ ভিত্তিক লগারিদম" }, example: "log(100) = 2" },
  { fn: "ln", meaning: { en: "Natural logarithm (base e)", bn: "প্রাকৃতিক লগারিদম (e ভিত্তিক)" }, example: "ln(e) = 1" },
  { fn: "x^y", meaning: { en: "x raised to the power y", bn: "x এর y তম ঘাত" }, example: "2^10 = 1024" },
  { fn: "sqrt, cbrt", meaning: { en: "Square root, cube root", bn: "বর্গমূল, ঘনমূল" }, example: "sqrt(16) = 4" },
  { fn: "n!", meaning: { en: "Factorial", bn: "ফ্যাক্টোরিয়াল" }, example: "5! = 120" },
  { fn: "abs(x)", meaning: { en: "Absolute value", bn: "পরম মান" }, example: "abs(-7) = 7" },
  { fn: "mod", meaning: { en: "Modulo (remainder)", bn: "ভাগশেষ" }, example: "10 mod 3 = 1" },
  { fn: "π, e", meaning: { en: "Constants", bn: "ধ্রুবক" }, example: "π ≈ 3.14159" },
  { fn: "Ans", meaning: { en: "Last answer", bn: "শেষ ফলাফল" }, example: "Ans + 5" },
];

const SHORTCUTS: { keys: string; action: Bilingual }[] = [
  { keys: "0–9 . + − × ÷", action: { en: "Direct number / operator entry", bn: "সরাসরি সংখ্যা / অপারেটর" } },
  { keys: "Enter / =", action: { en: "Evaluate expression", bn: "ফলাফল বের করুন" } },
  { keys: "Backspace", action: { en: "Delete last character (DEL)", bn: "শেষ অক্ষর মুছুন (DEL)" } },
  { keys: "Esc", action: { en: "All clear (AC)", bn: "সব পরিষ্কার (AC)" } },
  { keys: "( )", action: { en: "Parentheses", bn: "বন্ধনী" } },
  { keys: "p", action: { en: "Insert π", bn: "π বসান" } },
  { keys: "^", action: { en: "Power operator", bn: "ঘাত অপারেটর" } },
];

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How do I use sin, cos and tan?", bn: "sin, cos, tan কীভাবে ব্যবহার করব?" },
    a: {
      en: "Tap the function then type the angle inside the parentheses, e.g. sin(30). Make sure the angle mode (DEG or RAD) shown above the display matches what your question expects.",
      bn: "ফাংশন চাপুন, এরপর বন্ধনীর ভেতরে কোণ লিখুন — যেমন sin(30)। ডিসপ্লের উপরে দেখানো মোড (DEG বা RAD) প্রশ্ন অনুযায়ী আছে কি না নিশ্চিত করুন।",
    },
  },
  {
    q: { en: "What's the difference between DEG and RAD?", bn: "DEG ও RAD এর মধ্যে পার্থক্য কী?" },
    a: {
      en: "DEG measures angles in degrees (a full circle = 360°). RAD measures in radians (a full circle = 2π). The same trigonometric function returns very different numbers in each mode, so always check the indicator first.",
      bn: "DEG কোণ ডিগ্রিতে মাপে (পূর্ণ বৃত্ত = ৩৬০°)। RAD কোণ রেডিয়ানে মাপে (পূর্ণ বৃত্ত = ২π)। একই ফাংশন দুই মোডে ভিন্ন ফল দেয়, তাই আগে মোড দেখে নিন।",
    },
  },
  {
    q: { en: "How do I calculate factorial?", bn: "ফ্যাক্টোরিয়াল কীভাবে বের করব?" },
    a: {
      en: "Type the number followed by ! — for example 5! evaluates to 120. You can also use the n! button accessed via SHIFT + ).",
      bn: "সংখ্যার পরে ! লিখুন — যেমন 5! = 120। SHIFT + ) দিয়ে n! বাটনও ব্যবহার করা যায়।",
    },
  },
  {
    q: { en: "Can I use this calculator in the SSC or HSC exam?", bn: "এটি কি SSC বা HSC পরীক্ষায় ব্যবহার করা যাবে?" },
    a: {
      en: "No — Bangladesh education boards only allow approved physical calculators inside the exam hall. This online tool is for practice, homework and tuition.",
      bn: "না — বাংলাদেশের শিক্ষা বোর্ড পরীক্ষার হলে শুধু অনুমোদিত ফিজিক্যাল ক্যালকুলেটরই অনুমোদন করে। এটি অনুশীলন, হোমওয়ার্ক ও টিউশনের জন্য।",
    },
  },
  {
    q: { en: "How accurate is this calculator?", bn: "এই ক্যালকুলেটর কতটা সঠিক?" },
    a: {
      en: "Calculations use the well-tested mathjs library at IEEE-754 double precision — accurate to about 15 significant digits, the same precision as a Casio fx-991 in normal mode.",
      bn: "গণনা mathjs লাইব্রেরিতে IEEE-৭৫৪ ডাবল প্রিসিশনে হয় — প্রায় ১৫টি সার্থক অঙ্ক পর্যন্ত সঠিক, যা সাধারণ মোডে Casio fx-991 এর সমান।",
    },
  },
  {
    q: { en: "সাইন্টিফিক ক্যালকুলেটর কীভাবে ব্যবহার করব?", bn: "সাইন্টিফিক ক্যালকুলেটর কীভাবে ব্যবহার করব?" },
    a: {
      en: "কীবোর্ডে সরাসরি লিখুন বা স্ক্রিনের বাটনে চাপ দিন। SHIFT দিয়ে প্রতিটি বাটনের সেকেন্ডারি ফাংশন পাবেন। ফলাফল পেতে = বা Enter চাপুন। কোণ মোড (DEG/RAD) প্রশ্ন অনুযায়ী ঠিক রাখুন।",
      bn: "কীবোর্ডে সরাসরি লিখুন বা স্ক্রিনের বাটনে চাপ দিন। SHIFT দিয়ে প্রতিটি বাটনের সেকেন্ডারি ফাংশন পাবেন। ফলাফল পেতে = বা Enter চাপুন। কোণ মোড (DEG/RAD) প্রশ্ন অনুযায়ী ঠিক রাখুন।",
    },
  },
];

/* ------------ Button definitions ------------ */

type BtnKind = "num" | "op" | "fn" | "eq" | "del" | "ctrl";

interface Btn {
  /** Primary visible label */
  label: string;
  /** Label when SHIFT is active */
  shiftLabel?: string;
  /** Visual color */
  kind: BtnKind;
  /** Action on press; returns nothing — uses parent helpers via closure */
  onPress: (h: Helpers) => void;
  shiftPress?: (h: Helpers) => void;
  /** Used to keep keys stable */
  key: string;
  /** When set, indicates this row contains the SHIFT toggle */
  isShift?: boolean;
}

interface Helpers {
  insert: (s: string) => void;
  del: () => void;
  ac: () => void;
  evaluate: () => void;
  toggleSign: () => void;
  toggleShift: () => void;
}

/* ------------ Route ------------ */

export const Route = createFileRoute("/scientific-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Scientific Calculator Online — Free Casio Style",
      description:
        "Free scientific calculator: trigonometry, logarithm, exponents, factorials. Casio fx-991 style for SSC, HSC and university students in Bangladesh.",
      path: "/scientific-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Scientific Calculator", path: "/scientific-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: ScientificCalculatorPage,
});

/* ------------ Page ------------ */

const HISTORY_KEY = "bangla-calc-sci-history";
const MAX_HISTORY = 20;

interface HistoryEntry {
  expr: string;
  result: string;
}

function ScientificCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];

  const [expr, setExpr] = useState<string>("");
  const [result, setResult] = useState<string>("0");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [angleMode, setAngleMode] = useState<AngleMode>("DEG");
  const [displayMode, setDisplayMode] = useState<DisplayMode>("NORM");
  const [fixDigits, setFixDigits] = useState<number>(6);
  const [shift, setShift] = useState(false);
  const [memory, setMemory] = useState<number>(0);
  const [ans, setAns] = useState<number | null>(null);
  const [bnDigits, setBnDigits] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  

  // Load history once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
    } catch {
      // ignore
    }
  }, [history]);

  /* ---- Helpers ---- */
  const insert = useCallback((s: string) => {
    setErrorMsg(null);
    setExpr((prev) => prev + s);
    setShift(false);
  }, []);
  const del = useCallback(() => {
    setErrorMsg(null);
    setExpr((p) => p.slice(0, -1));
  }, []);
  const ac = useCallback(() => {
    setExpr("");
    setResult("0");
    setErrorMsg(null);
  }, []);
  const toggleSign = useCallback(() => {
    setExpr((prev) => {
      // Toggle a unary minus on the trailing number/group
      const m = prev.match(/(.*?)(-?)([\d.]+|\([^)]*\))$/);
      if (!m) return prev + "-";
      const [, head, sign, tail] = m;
      return head + (sign ? "" : "-") + tail;
    });
  }, []);
  const toggleShift = useCallback(() => setShift((s) => !s), []);

  const evaluateNow = useCallback(() => {
    if (!expr.trim()) return;
    const r = evaluateExpression(expr, { angleMode, displayMode, fixDigits, ans, memory });
    if (r.ok) {
      setResult(r.display);
      setErrorMsg(null);
      setAns(r.value);
      setHistory((h) => [{ expr, result: r.display }, ...h].slice(0, MAX_HISTORY));
    } else {
      setErrorMsg(r.message);
    }
  }, [expr, angleMode, displayMode, fixDigits, ans, memory]);

  const helpers: Helpers = useMemo(
    () => ({ insert, del, ac, evaluate: evaluateNow, toggleSign, toggleShift }),
    [insert, del, ac, evaluateNow, toggleSign, toggleShift],
  );

  /* ---- Keyboard input ---- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore when focus is inside an editable element other than our shell
      const tgt = e.target as HTMLElement | null;
      if (tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable)) return;
      const k = e.key;
      if (/^[0-9.]$/.test(k)) return insert(k);
      if (k === "+" || k === "-") return insert(k);
      if (k === "*") return insert("×");
      if (k === "/") return insert("÷");
      if (k === "(" || k === ")" || k === "%" || k === "^" || k === "!") return insert(k);
      if (k === "p" || k === "P") return insert("π");
      if (k === "e" || k === "E") return insert("e");
      if (k === "Enter" || k === "=") {
        e.preventDefault();
        return evaluateNow();
      }
      if (k === "Backspace") {
        e.preventDefault();
        return del();
      }
      if (k === "Escape") return ac();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [insert, evaluateNow, del, ac]);

  /* ---- Memory ops ---- */
  const memAdd = () => ans != null && setMemory((m) => m + ans);
  const memSub = () => ans != null && setMemory((m) => m - ans);
  const memRecall = () => insert(String(memory));
  const memClear = () => setMemory(0);

  const reuseHistory = (h: HistoryEntry) => {
    setExpr(h.expr);
    setErrorMsg(null);
    setHistoryOpen(false);
  };
  const clearHistory = () => setHistory([]);

  /* ---- Buttons grid ---- */
  const rows: Btn[][] = useMemo(() => {
    const ins = (s: string) => (h: Helpers) => h.insert(s);
    return [
      // Row 1
      [
        { key: "shift", label: "SHIFT", kind: "ctrl", onPress: (h) => h.toggleShift(), isShift: true },
        { key: "alpha", label: "ALPHA", kind: "ctrl", onPress: ins("Ans") },
        { key: "mode", label: "MODE", kind: "ctrl", onPress: () => setAngleMode((m) => (m === "DEG" ? "RAD" : "DEG")) },
        { key: "hist", label: "HIST", kind: "ctrl", onPress: () => setHistoryOpen(true) },
        { key: "ac", label: "AC", kind: "del", onPress: (h) => h.ac() },
      ],
      // Row 2
      [
        { key: "x2", label: "x²", shiftLabel: "√x", kind: "fn", onPress: ins("^2"), shiftPress: ins("sqrt(") },
        { key: "x3", label: "x³", kind: "fn", onPress: ins("^3") },
        { key: "xy", label: "xʸ", kind: "fn", onPress: ins("^") },
        { key: "sqrt", label: "√", kind: "fn", onPress: ins("sqrt(") },
        { key: "cbrt", label: "∛", kind: "fn", onPress: ins("cbrt(") },
      ],
      // Row 3
      [
        { key: "sin", label: "sin", shiftLabel: "sin⁻¹", kind: "fn", onPress: ins("sin("), shiftPress: ins("asin(") },
        { key: "cos", label: "cos", shiftLabel: "cos⁻¹", kind: "fn", onPress: ins("cos("), shiftPress: ins("acos(") },
        { key: "tan", label: "tan", shiftLabel: "tan⁻¹", kind: "fn", onPress: ins("tan("), shiftPress: ins("atan(") },
        { key: "log", label: "log", shiftLabel: "10ˣ", kind: "fn", onPress: ins("log("), shiftPress: ins("10^") },
        { key: "ln", label: "ln", shiftLabel: "eˣ", kind: "fn", onPress: ins("ln("), shiftPress: ins("e^") },
      ],
      // Row 4
      [
        { key: "lp", label: "(", shiftLabel: "|x|", kind: "op", onPress: ins("("), shiftPress: ins("abs(") },
        { key: "rp", label: ")", shiftLabel: "n!", kind: "op", onPress: ins(")"), shiftPress: ins("!") },
        { key: "pi", label: "π", kind: "fn", onPress: ins("π") },
        { key: "e", label: "e", kind: "fn", onPress: ins("e") },
        { key: "pct", label: "%", kind: "op", onPress: ins("%") },
      ],
      // Row 5
      [
        { key: "7", label: "7", kind: "num", onPress: ins("7") },
        { key: "8", label: "8", kind: "num", onPress: ins("8") },
        { key: "9", label: "9", kind: "num", onPress: ins("9") },
        { key: "del", label: "DEL", kind: "del", onPress: (h) => h.del() },
        { key: "div", label: "÷", kind: "op", onPress: ins("÷") },
      ],
      // Row 6
      [
        { key: "4", label: "4", kind: "num", onPress: ins("4") },
        { key: "5", label: "5", kind: "num", onPress: ins("5") },
        { key: "6", label: "6", kind: "num", onPress: ins("6") },
        { key: "mul", label: "×", kind: "op", onPress: ins("×") },
        { key: "sign", label: "±", kind: "op", onPress: (h) => h.toggleSign() },
      ],
      // Row 7
      [
        { key: "1", label: "1", kind: "num", onPress: ins("1") },
        { key: "2", label: "2", kind: "num", onPress: ins("2") },
        { key: "3", label: "3", kind: "num", onPress: ins("3") },
        { key: "minus", label: "−", kind: "op", onPress: ins("-") },
        { key: "ans", label: "Ans", kind: "fn", onPress: ins("Ans") },
      ],
      // Row 8
      [
        { key: "0", label: "0", kind: "num", onPress: ins("0") },
        { key: "dot", label: ".", kind: "num", onPress: ins(".") },
        { key: "exp", label: "EXP", kind: "fn", onPress: ins("*10^") },
        { key: "plus", label: "+", kind: "op", onPress: ins("+") },
        { key: "eq", label: "=", kind: "eq", onPress: (h) => h.evaluate() },
      ],
    ];
  }, []);

  const formatDisplay = (s: string) => (bnDigits ? toBengaliNumerals(s) : s);

  return (
    <CalculatorLayout
      slug="scientific-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="FunctionSquare"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="grid gap-4 lg:grid-cols-[1fr_240px]">
        {/* Calculator */}
        <div>
          {/* Mode bar */}
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <UnitToggle<AngleMode>
              label={tr("angle")}
              value={angleMode}
              onChange={setAngleMode}
              options={[
                { value: "DEG", label: "DEG" },
                { value: "RAD", label: "RAD" },
              ]}
            />
            <UnitToggle<DisplayMode>
              label={tr("display")}
              value={displayMode}
              onChange={setDisplayMode}
              options={[
                { value: "NORM", label: "NORM" },
                { value: "FIX", label: "FIX" },
                { value: "SCI", label: "SCI" },
                { value: "ENG", label: "ENG" },
              ]}
            />
            {(displayMode === "FIX" || displayMode === "SCI" || displayMode === "ENG") && (
              <div className="space-y-1.5">
                <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("fix")}</label>
                <select
                  value={fixDigits}
                  onChange={(e) => setFixDigits(Number(e.target.value))}
                  className="rounded-md border border-border bg-muted px-2 py-1.5 text-sm"
                >
                  {Array.from({ length: 10 }, (_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <label className={cn("ml-auto inline-flex select-none items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              <input
                type="checkbox"
                checked={bnDigits}
                onChange={(e) => setBnDigits(e.target.checked)}
                className="h-3.5 w-3.5 accent-accent"
              />
              {tr("bnNumerals")}
            </label>
          </div>

          {/* Display */}
          <div className="rounded-xl border border-border bg-foreground/5 p-4 shadow-inner">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="rounded bg-accent/15 px-1.5 py-0.5 text-accent">{angleMode}</span>
              <span className="rounded bg-muted px-1.5 py-0.5">{displayMode}</span>
              {memory !== 0 && <span className="rounded bg-muted px-1.5 py-0.5">M</span>}
              {shift && <span className="rounded bg-yellow-400/20 px-1.5 py-0.5 text-yellow-600 dark:text-yellow-400">{tr("shiftActive")}</span>}
              <span className="ml-auto">{history.length}/{MAX_HISTORY} {tr("history")}</span>
            </div>
            <p
              className={cn(
                "min-h-[1.25rem] truncate text-right font-mono text-sm text-muted-foreground",
                !expr && "italic",
              )}
              title={expr}
            >
              {expr ? formatDisplay(expr) : tr("expressionPh")}
            </p>
            <p
              className={cn(
                "mt-1 break-all text-right font-mono text-3xl font-semibold tabular-nums md:text-4xl",
                errorMsg ? "text-destructive" : "text-foreground",
              )}
            >
              {errorMsg ?? formatDisplay(result)}
            </p>
          </div>

          {/* Memory row */}
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { label: "M+", fn: memAdd },
              { label: "M−", fn: memSub },
              { label: "MR", fn: memRecall },
              { label: "MC", fn: memClear },
            ].map((b) => (
              <button
                key={b.label}
                type="button"
                onClick={b.fn}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground/80 transition hover:bg-muted"
              >
                {b.label}
              </button>
            ))}
          </div>

          {/* Buttons */}
          <div className="mt-4 grid grid-cols-5 gap-2">
            {rows.flat().map((b) => (
              <CalcBtn key={b.key} btn={b} shift={shift} helpers={helpers} />
            ))}
          </div>
        </div>

        {/* History panel */}
        <aside
          className={cn(
            "rounded-xl border border-border bg-card p-3",
            "lg:block",
            historyOpen ? "block" : "hidden lg:block",
          )}
        >
          <div className="mb-2 flex items-center justify-between">
            <p className={cn("inline-flex items-center gap-1.5 text-sm font-semibold", lang === "bn" && "font-bn")}>
              <HistoryIcon size={14} className="text-accent" />
              {tr("history")}
            </p>
            <div className="flex items-center gap-1">
              {history.length > 0 && (
                <button
                  type="button"
                  onClick={clearHistory}
                  className="inline-flex items-center gap-1 rounded p-1 text-xs text-muted-foreground hover:text-destructive"
                  title={tr("clearHistory")}
                >
                  <Trash2 size={12} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                className="rounded p-1 text-muted-foreground hover:text-foreground lg:hidden"
              >
                <ArrowLeft size={14} />
              </button>
            </div>
          </div>

          {history.length === 0 ? (
            <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("noHistory")}</p>
          ) : (
            <ul className="max-h-72 space-y-1 overflow-y-auto pr-1 lg:max-h-[28rem]">
              {history.map((h, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => reuseHistory(h)}
                    className="w-full rounded-md border border-transparent px-2 py-1.5 text-left transition hover:border-border hover:bg-muted/60"
                  >
                    <p className="truncate font-mono text-xs text-muted-foreground" title={h.expr}>
                      {formatDisplay(h.expr)}
                    </p>
                    <p className="truncate text-right font-mono text-sm font-semibold text-foreground" title={h.result}>
                      = {formatDisplay(h.result)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {/* Floating reset bar (mobile-friendly history toggle) */}
      <div className="mt-4 flex items-center gap-2 lg:hidden">
        <Button variant="outline" size="sm" onClick={() => setHistoryOpen((o) => !o)} className="gap-1.5">
          <HistoryIcon size={14} />
          {tr("history")}
        </Button>
        <Button variant="outline" size="sm" onClick={ac} className="gap-1.5">
          <RotateCcw size={14} />
          AC
        </Button>
      </div>

      {/* Content */}
      <article className="mt-12 space-y-10">
        <Section title={tr("howTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("howBody")}</p>
        </Section>

        <Section title={tr("refTitle")} lang={lang}>
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Function</th>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{lang === "bn" ? "অর্থ" : "Meaning"}</th>
                  <th className="px-3 py-2 text-left">Example</th>
                </tr>
              </thead>
              <tbody>
                {FUNC_REF.map((f) => (
                  <tr key={f.fn} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-foreground">{f.fn}</td>
                    <td className={cn("px-3 py-2 text-muted-foreground", lang === "bn" && "font-bn")}>{f.meaning[lang]}</td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">{f.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={tr("modeTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("modeBody")}</p>
        </Section>

        <Section title={tr("shortcutsTitle")} lang={lang}>
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <tbody>
                {SHORTCUTS.map((s, i) => (
                  <tr key={i} className={i ? "border-t border-border" : ""}>
                    <td className="px-3 py-2 font-mono text-foreground">{s.keys}</td>
                    <td className={cn("px-3 py-2 text-muted-foreground", lang === "bn" && "font-bn")}>{s.action[lang]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={tr("sscTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("sscBody")}</p>
        </Section>

        <Section title={tr("hscTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("hscBody")}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

/* ------------ Calc button ------------ */

function CalcBtn({ btn, shift, helpers }: { btn: Btn; shift: boolean; helpers: Helpers }) {
  const showShift = shift && btn.shiftLabel;
  const onClick = () => {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      try {
        (navigator as Navigator & { vibrate: (n: number) => void }).vibrate(8);
      } catch {
        // ignore
      }
    }
    if (showShift && btn.shiftPress) btn.shiftPress(helpers);
    else btn.onPress(helpers);
  };

  const colorClass = (() => {
    if (btn.kind === "eq") return "bg-accent text-accent-foreground hover:bg-accent/90";
    if (btn.kind === "del") return "bg-destructive/10 text-destructive hover:bg-destructive/20";
    if (btn.kind === "op") return "bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 dark:text-sky-300";
    if (btn.kind === "fn") return "bg-indigo-500/10 text-indigo-700 hover:bg-indigo-500/20 dark:text-indigo-300";
    if (btn.kind === "ctrl") {
      if (btn.isShift && shift) return "bg-yellow-400 text-foreground hover:bg-yellow-400/90";
      return "bg-foreground/5 text-foreground hover:bg-foreground/10";
    }
    return "bg-card text-foreground hover:bg-muted";
  })();

  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.94 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      onClick={onClick}
      className={cn(
        "relative rounded-lg border border-border px-1 py-3 text-sm font-semibold tabular-nums shadow-sm transition active:shadow-inner sm:text-base",
        colorClass,
      )}
    >
      {btn.shiftLabel && (
        <span className="pointer-events-none absolute left-1 top-0.5 text-[9px] font-medium leading-none text-yellow-600 dark:text-yellow-400">
          {btn.shiftLabel}
        </span>
      )}
      <span>{showShift ? btn.shiftLabel : btn.label}</span>
    </motion.button>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
